import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ModalResponsive,
  Loading,
  LoadingCircle,
  Pagination,
  UsersCard,
  UsersCardTeam,
  Tooltip,
} from '../../../components';
import Deactivate from '../../../assets/buttons/deactivate.svg';
import Reactivate from '../../../assets/buttons/reactivate.svg';
import GroupAdd from '../../../assets/buttons/group-add.svg';
import { useAccess, useClient, useStorage } from '../../../hooks';
import {
  searchArrayOfObjects,
  sendNotification,
  formatDocumentCNPJ,
} from '../../../utils/functions';
import makeAnimated from 'react-select/animated';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  WithContext as ReactTags,
  type Tag as ReactTag,
} from 'react-tag-input';

import { FaRegCircleQuestion } from 'react-icons/fa6';
import { MdOutlineEdit } from 'react-icons/md';
import { Container, Content } from '../../../styles/lib/grid';
import { TabsContainer } from '../../../styles/lib/tabs';
import * as S from './UsersStyles';
import { RegistrationClientForm } from './Components/RegistrationClientForm/RegistrationClientForm';
import ValidationUserDialog from './Components/ValidationUser/ValidationDialog';
import { Select, Option } from '../../../styles/lib/dynamic-filter';
import { IProfileGroup } from '../../../interfaces/permissions';
import { AxiosResponse } from 'axios';
import { usePagination } from '../../../hooks/usePagination';
import { TrashIcon } from 'lucide-react';

const toastConfig = {
  autoClose: 4000,
  hideProgressBar: true,
  type: 'success',
};

const errorToastConfig = {
  autoClose: 3000,
  hideProgressBar: true,
  type: 'success',
};

export const Users: React.FC = () => {
  const {
    returnAllUsers,
    changeUserStatus,
    editUserInfo,
    analyzeUser,
    makeUserAdmin,
    deleteUser,
    isLoading,
    isLoadingAPICall,
    representantes,
    metadata,
  } = useAccess();

  const [
    { profileGroups, responsibleChannel },
    fetchProfilesGroups,
    ,
    ,
    ,
    ,
    fetchResponsibleChannel,
  ] = usePermissions();

  const { getItemOnStorage } = useStorage();

  const [apiError, setApiError] = useState<string | null>(null);

  const [channel, setChannel] = useState<any>([]);
  const [profile, setProfile] = useState('');
  const [cnpjsRaiz, setCnpjsRaiz] = useState<any>([]);
  const [cnpjTagInput, setCnpjTagInput] = useState('');
  const [{ reject }, consultingCnpj, clearReject] = useClient();

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAnalysisModal, setOpenAnalysisModal] = useState(false);
  const [openAnalysisModalRevendedor, setOpenAnalysisModalRevendedor] =
    useState(false);
  const [openReactivateModal, setOpenReactivateModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [openAdminModal, setOpenAdminModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [filters, setFilters] = useState<{
    type: string;
    term: string;
    tipoPerfil: string;
  }>({
    type: '',
    term: '',
    tipoPerfil: '',
  });

  const [user, setUser] = useState<any>(null);
  const [isApprove, setIsApprove] = useState(true);
  const [reproveReason, setReproveReason] = useState('');
  const [editDealer, setEditDealer] = useState(false);

  function getCurrentUser<T = any>(): T | null {
    try {
      const data = getItemOnStorage('currentUser');
      if (!data) return null;
      return JSON.parse(data) as T;
    } catch (err) {
      return null;
    }
  }

  const [currentUser, setCurrentUser] = useState<any>(() => getCurrentUser());

  useEffect(() => {
    fetchProfilesGroups({ active: true });
    fetchResponsibleChannel();
    handleAllUsers();
    setApiError(null);
  }, []);

  const animatedSelectComponents = makeAnimated();
  const [state, setState] = useState({ reject: '' });

  const clearCnpjError = () => {
    clearReject();
  };

  useEffect(() => {
    if (openEditModal && user) {
      setCnpjsRaiz([]);
      setCnpjTagInput('');
      setChannel([]);
      setProfile('');
      clearCnpjError();

      treatmentUserAttributes(user);
      patchCnpjsRaiz(user);
    } else if (!openEditModal) {
      setCnpjsRaiz([]);
      setCnpjTagInput('');
      setChannel([]);
      setProfile('');
      clearCnpjError();
    }
  }, [openEditModal, user?.id]);

  const profileTypes = useMemo(() => {
    return profileGroups.map((e: any) => {
      return e;
    });
  }, [profileGroups]);

  const handleChannelChoosen = (channel: any, user: any) => {
    setUser({ ...user, canaisResponsaveis: channel });
    setChannel(channel);
  };

  const { currentPage, pageSize, handlePageChange, resetPagination } =
    usePagination({
      totalItems: representantes.pagination?.totalItems || 0,
      onPageChange: (currentPage, pageSize) => {
        returnAllUsers({
          ...filters,
          currentPage,
          pageSize,
          isNew: false,
        });
      },
    });

  const handleAllUsers = () => {
    resetPagination();
    returnAllUsers({
      ...filters,
      pageSize,
      currentPage: 1,
      isNew: false,
    });
  };

  const searchUsers = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleAllUsers();
  };

  const handleModalClose = async () => {
    setOpenRegisterModal(false);
    handleAllUsers();
  };

  const handleUserAction = async (
    actionFn: () => Promise<AxiosResponse | undefined>,
    successMessage: string,
    errorMessage?: string,
  ) => {
    try {
      await actionFn();
      sendNotification(successMessage, toastConfig);
      setOpenAnalysisModal(false);
      setOpenEditModal(false);
      setReproveReason('');
      setApiError(null);
    } catch (e: any) {
      const errorToDisplay =
        errorMessage || e.response?.data?.errors || e.message;
      setApiError(errorToDisplay);
    }
  };

  const handleDeleteUser = async (idUser: string | number) => {
    if (!idUser)
      return sendNotification('ID do usuário inválido.', {
        ...toastConfig,
        type: 'error',
      });

    try {
      const { data } = await deleteUser(idUser);
      if (data?.success) {
        sendNotification(
          `Usuário ${data?.data?.nome ?? ''} excluído com sucesso.`,
          toastConfig,
        );
        setOpenDeleteModal(false);
        handleAllUsers();
      } else {
        throw new Error(data?.errors?.join(', ') || 'Falha na API.');
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.errors?.join(', ') ||
        error?.message ||
        'Erro ao excluir usuário. Tente novamente.';
      console.error('Erro:', error);
      sendNotification(`Erro: ${msg}`, {
        ...toastConfig,
        type: 'error',
        autoClose: 6000,
      });
    }
  };

  const patchCnpjsRaiz = (user: any) => {
    if (!user?.cnpj) {
      setCnpjsRaiz([]);
      return;
    }

    const cnpjs = Array.isArray(user.cnpj)
      ? user.cnpj
      : typeof user.cnpj === 'string'
      ? [user.cnpj]
      : [];

    const formatted = cnpjs.map((cnpj: any) => ({
      id: String(cnpj),
      text: formatCnpj(String(cnpj)),
    }));

    setCnpjsRaiz(formatted);
  };

  const formatCnpj = (cnpj: string) => {
    if (typeof cnpj !== 'string') {
      return cnpj;
    }
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(
      5,
      8,
    )}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`;
  };

  const updateUser = async () => {
    const response: any = await editUserInfo({
      ...user,
      canaisResponsaveis: channel,
      cnpjsRaiz: cnpjsRaiz.map((i: { id: string; text: string }) =>
        i.id.replace(/[./-]/g, '').toUpperCase(),
      ),
    });
    if (response?.status >= 200 && response?.status < 300) {
      setOpenEditModal(false);
      sendNotification(
        `As alterações no usuário ${
          user?.nome ?? user?.nomeContato ?? user?.razaoSocial
        } foram realizadas com sucesso.`,
        toastConfig,
      );

      handleAllUsers();
    }
  };
  const approveUser = () => {
    handleUserAction(
      () => analyzeUser(user?.id, isApprove, reproveReason, false),
      `O usuário ${user?.razaoSocial} foi aprovado.`,
    );
  };

  const repproveUser = async () => {
    handleUserAction(
      () => analyzeUser(user?.id, isApprove, reproveReason, false),
      `O usuário ${user?.razaoSocial ? user?.razaoSocial : ''} foi reprovado.`,
    );
  };

  const activateUser = async () => {
    await changeUserStatus(user?.id, true);
    sendNotification(
      `O usuário ${user?.razaoSocial} foi reativado.`,
      toastConfig,
    );
    setOpenReactivateModal(false);

    handleAllUsers();
  };

  const deactivateUser = async () => {
    await changeUserStatus(user?.id, false);
    sendNotification(
      `O usuário ${user?.razaoSocial} foi desativado.`,
      toastConfig,
    );
    setOpenDeactivateModal(false);
    handleAllUsers();
  };

  const isRevendedor = (perfil: string) => {
    return perfil === 'revendedor-allied';
  };

  const renderGrupoEconomico = (grupoEconomico: any) => {
    if (
      !grupoEconomico ||
      !Array?.isArray(grupoEconomico) ||
      grupoEconomico?.length === 0
    ) {
      return '-';
    }

    const primary =
      typeof grupoEconomico?.[0] === 'string'
        ? grupoEconomico?.[0]
        : 'Principal';
    const others = Array.isArray(grupoEconomico)
      ? grupoEconomico?.slice(1)
      : [];

    return (
      <S.ListInline>
        <ul className="no-flex">
          <li>
            <span>Principal</span> {primary}
          </li>
          {others?.length > 0 && (
            <li className="same">
              <span>Outros</span> {others.join(', ')}
            </li>
          )}
        </ul>
      </S.ListInline>
    );
  };

  const treatmentUserAttributes = (userData: any) => {
    if (!userData) return;

    setUser(userData);
    setProfile(userData.perfil?.nome || '');

    if (userData?.canaisResponsaveis?.length > 0) {
      const canaisFormatados = userData.canaisResponsaveis.map((r: any) => {
        const matching = responsibleChannel.find(
          (channel: any) => channel.label === r,
        );
        return {
          value: matching?.value || null,
          label: r,
        };
      });
      setChannel(canaisFormatados);
    } else {
      setChannel([]);
    }
  };

  const formatCNPJInput = (value: string): string => {
    const raw = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    const body = raw.slice(0, 12);
    const dv = raw
      .slice(12)
      .replace(/[^0-9]/g, '')
      .slice(0, 2);
    const limited = body + dv;

    let result = '';
    for (let i = 0; i < limited.length; i++) {
      if (i === 2 || i === 5) result += '.';
      if (i === 8) result += '/';
      if (i === 12) result += '-';
      result += limited[i];
    }

    return result;
  };

  const handleAdditionCnpjs = async (tag: ReactTag) => {
    const cnpjText: string = tag.text
      .replace(/[./-]/g, '')
      .toUpperCase()
      .trim();

    if (!cnpjText || cnpjText.length > 14) {
      setCnpjTagInput('');
      return;
    }

    const formattedCnpj = formatDocumentCNPJ(cnpjText);

    try {
      const responseConsulted = await consultingCnpj(cnpjText);
      const grupoEconomico = responseConsulted?.cliente?.grupoEconomico || '';

      if (grupoEconomico && !user?.grupoEconomico?.includes(grupoEconomico)) {
        const newCnpj = { id: cnpjText, text: formattedCnpj };

        setCnpjsRaiz((prev: any) => [...prev, newCnpj]);
        setUser((prev: any) => ({
          ...prev,
          cnpj: [...(prev.cnpj ?? []), cnpjText],
          grupoEconomico: [...(prev.grupoEconomico ?? []), grupoEconomico],
        }));

        setCnpjTagInput('');
      } else {
        setCnpjTagInput('');
        sendNotification('Nenhum registro encontrado para o CNPJ informado.', {
          ...toastConfig,
          type: 'error',
        });
      }
    } catch (error) {
      console.error(error);
      setCnpjTagInput('');
      sendNotification('Erro ao consultar CNPJ. Tente novamente.', {
        ...toastConfig,
        type: 'error',
      });
    }
  };

  const handleDeleteCnpjs = (index: number) => {
    if (index < 0 || !Array.isArray(cnpjsRaiz)) return;

    const newCnpjsRaiz = cnpjsRaiz.filter((_: any, i: number) => i !== index);
    setCnpjsRaiz(newCnpjsRaiz);

    setUser((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        cnpj: Array.isArray(prev.cnpj)
          ? prev.cnpj.filter((_: any, i: number) => i !== index)
          : [],
        grupoEconomico: Array.isArray(prev.grupoEconomico)
          ? prev.grupoEconomico.filter((_: any, i: number) => i !== index)
          : [],
      };
    });
  };

  const handleCnpjsDrag = (_tag: ReactTag, currPos: number, newPos: number) => {
    const newCnpjs = [...cnpjsRaiz];
    const [movedCnpj] = newCnpjs.splice(currPos, 1);
    newCnpjs.splice(newPos, 0, movedCnpj);
    setCnpjsRaiz(newCnpjs);

    const newUserCnpjs = [...user.cnpj];
    const [movedUserCnpj] = newUserCnpjs.splice(currPos, 1);
    newUserCnpjs.splice(newPos, 0, movedUserCnpj);

    const newGrupoEconomico = [...user.grupoEconomico];
    const [movedGrupoEconomico] = newGrupoEconomico.splice(currPos, 1);
    newGrupoEconomico.splice(newPos, 0, movedGrupoEconomico);

    setUser({
      ...user,
      cnpj: newUserCnpjs,
      grupoEconomico: newGrupoEconomico,
    });
  };

  const handleMakeAdmin = async () => {
    if (!user?.id)
      return sendNotification('ID do usuário inválido.', {
        ...toastConfig,
        type: 'error',
      });

    try {
      const { data } = await makeUserAdmin(user.id);
      if (data?.data?.sucesso) {
        sendNotification(
          `Usuário ${user?.nome ?? ''} transformado em administrador.`,
          toastConfig,
        );
        setOpenAdminModal(false);
        handleAllUsers();
      } else {
        throw new Error(data?.errors?.join(', ') || 'Falha na API.');
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.errors?.join(', ') ||
        error?.message ||
        'Erro ao transformar em admin. Tente novamente.';
      console.error('Erro:', error);
      sendNotification(`Erro: ${msg}`, {
        ...toastConfig,
        type: 'error',
        autoClose: 6000,
      });
    }
  };

  const renderRepresentantes = (representantes: any) => {
    const cadastrados = representantes?.filter(
      (representante: any) => representante?.analisado,
    );

    return (
      <>
        <h2>Cadastrados</h2>
        <hr style={{ margin: '10px 0 20px 0' }} />
        {cadastrados?.map((elm: any) => (
          <UsersCard key={elm?.id} user={elm}>
            <S.GroupContent>
              <S.CardButtons>
                {elm?.ativo && elm?.analisado && (
                  <S.IconButton
                    className="deactivate"
                    onClick={() => {
                      setOpenDeactivateModal(true);
                      setUser(elm);
                    }}
                  >
                    <img src={Deactivate} width={16} />
                    Desativar
                  </S.IconButton>
                )}
                {!elm?.ativo && elm?.analisado && (
                  <S.IconButton
                    className="reactivate"
                    onClick={() => {
                      setOpenReactivateModal(true);
                      setUser(elm);
                    }}
                  >
                    <img src={Reactivate} width={16} />
                    Reativar
                  </S.IconButton>
                )}
                <S.IconButton
                  className={!elm.analisado ? 'edit disabled' : 'edit'}
                  onClick={() => {
                    setApiError(null);
                    treatmentUserAttributes(elm);
                    if (!isRevendedor(elm?.perfil?.nome)) {
                      setOpenEditModal(true);
                    } else {
                      setEditDealer(true);
                      setOpenAnalysisModalRevendedor(true);
                    }
                  }}
                  disabled={!elm?.analisado}
                >
                  <MdOutlineEdit color="#FFFFFF" size={16} />
                  Editar
                </S.IconButton>
                <S.IconButton
                  className="danger-outline"
                  onClick={() => {
                    setUser(elm);
                    setOpenDeleteModal(true);
                  }}
                >
                  <TrashIcon size={16} />
                  Excluir
                </S.IconButton>
              </S.CardButtons>
              {elm?.perfil?.nome !== 'administrador' &&
                currentUser?.perfil === 'administrador' && (
                  <S.IconButton
                    className="link-admin"
                    onClick={() => {
                      setUser(elm);
                      setOpenAdminModal(true);
                    }}
                  >
                    Tornar usuário admin
                  </S.IconButton>
                )}
            </S.GroupContent>
          </UsersCard>
        ))}
      </>
    );
  };

  return (
    <Container>
      <Content>
        <S.SearchWrapper>
          <S.SearchCell>
            <S.Search onSubmit={searchUsers}>
              <S.InputContainer>
                <S.Select
                  value={filters.type}
                  className="floating"
                  onChange={(e: any) => {
                    setFilters(prev => ({
                      ...prev,
                      type: e.target.value,
                      term: '',
                    }));
                  }}
                >
                  <S.Option value="">Selecione</S.Option>
                  <S.Option value="cnpj">CNPJ</S.Option>
                  <S.Option value="razao">Nome / Razão Social</S.Option>
                  <S.Option value="email">E-mail</S.Option>
                </S.Select>
                <S.Input
                  maxLength={35}
                  value={filters.term}
                  onChange={(e: any) =>
                    setFilters(prev => ({
                      ...prev,
                      term:
                        prev.type === 'cnpj'
                          ? formatDocumentCNPJ(e.target.value)
                          : e.target.value,
                    }))
                  }
                  placeholder={'Faça sua busca...'}
                />
              </S.InputContainer>

              <Select
                className="highlighted"
                value={filters.tipoPerfil}
                onChange={(e: any) =>
                  setFilters(prev => ({ ...prev, tipoPerfil: e.target.value }))
                }
              >
                <Option value="">Todos Perfis</Option>
                {profileGroups
                  .filter((group: IProfileGroup) => group.ativo)
                  .map((group: IProfileGroup, index: number) => (
                    <Option key={index} value={group.nome}>
                      {group.nome}
                    </Option>
                  ))}
              </Select>

              <S.ButtonsContainer>
                <S.Button type="submit">Buscar</S.Button>
              </S.ButtonsContainer>
            </S.Search>

            <S.CardText className="normal">
              Confira os dados, altere ou edite perfis de usuários na lista
              abaixo.
            </S.CardText>
          </S.SearchCell>

          <S.CardInfo>
            <S.CardCell className="left">
              <S.CardText className="active">Ativos</S.CardText>
              <p>
                <S.CardText className="bolder">
                  {metadata.totalAtivos ?? '-'}/
                </S.CardText>
                <S.CardText className="smallest">
                  {metadata.total ?? '-'}
                </S.CardText>{' '}
                <S.CardText className="info">usuários</S.CardText>
              </p>
            </S.CardCell>
            <S.CardCell>
              <S.CardText className="inactive">Inativos</S.CardText>
              <p>
                <S.CardText className="bolder">
                  {metadata.totalInativos ?? '-'}/
                </S.CardText>
                <S.CardText className="smallest">
                  {metadata.total ?? '-'}
                </S.CardText>{' '}
                <S.CardText className="info">usuários</S.CardText>
              </p>
            </S.CardCell>
          </S.CardInfo>
          <S.ButtonsContainer className="register">
            <S.IconButton
              className="primary medium"
              onClick={() => {
                setOpenRegisterModal(true);
              }}
            >
              <img src={GroupAdd} width={16} />
              Novo Cliente Final
            </S.IconButton>
          </S.ButtonsContainer>
        </S.SearchWrapper>

        <TabsContainer>
          {isLoading ? (
            <Loading />
          ) : (
            renderRepresentantes(representantes.representantes)
          )}
        </TabsContainer>

        {!isLoading && representantes.representantes?.length === 0 && (
          <S.Message>Nenhum resultado encontrado.</S.Message>
        )}

        {!isLoading && representantes.representantes?.length !== 0 && (
          <Pagination
            currentPage={currentPage}
            paginationSize={pageSize}
            onPageChange={handlePageChange}
            totalItems={representantes.pagination?.totalItems}
          />
        )}
      </Content>

      <>
        <ModalResponsive
          title={'Edição de usuário'}
          isOpenModal={openEditModal}
          handleOpenModal={setOpenEditModal}
          isSmall={false}
        >
          <S.Featured
            className="background flexing"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <div style={{ flex: 1 }}>
              {[
                {
                  label: 'Nome / Razão Social',
                  value: user?.nome ?? user?.nomeContato ?? user?.razaoSocial,
                },
                { label: 'E-mail', value: (user?.email ?? '-').toLowerCase() },
                { label: 'Perfil do usuário', value: user?.perfil?.nome },
              ].map((item, index) => (
                <React.Fragment key={index}>
                  {item.value !== undefined && (
                    <>
                      <S.Text className="bold top-margin">{item.label}</S.Text>
                      <S.Text>{item.value ?? '-'}</S.Text>
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div style={{ flex: 1 }}>
              <S.Text className="bold top-margin">
                Grupo(s) econômico(s)
                <Tooltip content="O grupo econômico principal é determinado a partir do primeiro CNPJ raiz informado.">
                  <FaRegCircleQuestion
                    style={{
                      width: '1.1em',
                      height: '1.1em',
                      marginLeft: '5px',
                      color: 'gray',
                    }}
                  />
                </Tooltip>
              </S.Text>
              {renderGrupoEconomico(user?.grupoEconomico)}{' '}
            </div>
          </S.Featured>

          <S.Featured>
            {user?.perfil?.nome !== 'cliente-final' && (
              <>
                <S.Text className="bold">Perfil do usuário</S.Text>
                <S.Select
                  value={profile}
                  onChange={(e: any) => {
                    setProfile(e.target.value);
                    setUser({
                      ...user,
                      perfil: searchArrayOfObjects(
                        profileTypes,
                        'nome',
                        e.target.value,
                      ),
                    });
                  }}
                >
                  {profileTypes.map((e: any) => {
                    return (
                      <S.Option key={e.id} value={e.nome}>
                        {e.nome}
                      </S.Option>
                    );
                  })}
                </S.Select>
              </>
            )}
            <S.FormControl>
              <S.Text className="bold top-margin">CNPJs Raiz</S.Text>
              <ReactTags
                classNames={{ tags: 'ReactTags__tags secondary' }}
                tags={cnpjsRaiz}
                handleAddition={handleAdditionCnpjs as any}
                handleDelete={handleDeleteCnpjs}
                handleDrag={handleCnpjsDrag as any}
                delimiters={[188, 13]}
                placeholder="Digite aqui os CNPJs..."
                inputValue={cnpjTagInput}
                handleInputChange={value =>
                  setCnpjTagInput(formatCNPJInput(value))
                }
                maxLength={18}
                allowDeleteFromEmptyInput={false}
              />
              {reject && <S.Text className="small-danger">{reject}</S.Text>}
            </S.FormControl>
            <S.FormControl>
              <S.Text className="bold">Canais Responsáveis</S.Text>
              <S.CustomMultiSelect
                classNamePrefix={'Select'}
                className="secondary"
                components={animatedSelectComponents}
                isMulti
                options={responsibleChannel}
                value={channel}
                onChange={(opts: any) => handleChannelChoosen(opts, user)}
                placeholder="Selecione"
                isClearable
              />
            </S.FormControl>
          </S.Featured>
          <S.Buttons>
            <S.IconButton
              className="text"
              onClick={() => setOpenEditModal(false)}
            >
              Cancelar
            </S.IconButton>
            <S.IconButton
              className="update"
              onClick={updateUser}
              disabled={isLoadingAPICall}
            >
              {isLoadingAPICall ? <LoadingCircle /> : 'Editar'}
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>

        <ValidationUserDialog
          isOpen={openAnalysisModalRevendedor}
          user={user}
          onClose={() => {
            setOpenAnalysisModalRevendedor(false);
            handleAllUsers();
          }}
          allowEdit={editDealer}
        />
        <ModalResponsive
          title={'Análise de novo acesso'}
          isOpenModal={openAnalysisModal}
          handleOpenModal={setOpenAnalysisModal}
        >
          <S.Text>
            Você está analisando a solicitação de acesso do usuário{' '}
            <S.Text className="bold inline">
              {user?.nome ?? user?.nomeContato ?? user?.razaoSocial}
            </S.Text>
            . Essa ação concederá ou rejeitará o acesso ao sistema. Você
            confirma?
          </S.Text>
          <S.RadioButtonWrapper>
            <label
              className="form-control"
              onClick={() => {
                setIsApprove(true);
              }}
            >
              <input type="radio" name="radio" checked={isApprove} />
              <span className={isApprove ? 'bolder' : ''}>
                Sim, liberar acesso ao usuário.
              </span>
            </label>

            <label
              className="form-control"
              onClick={() => {
                setIsApprove(false);
              }}
            >
              <input type="radio" name="radio" checked={!isApprove} />
              <span className={!isApprove ? 'bolder' : ''}>
                Não, negar acesso ao usuário.
              </span>
            </label>
          </S.RadioButtonWrapper>
          {!isApprove && (
            <S.InputReason
              value={reproveReason}
              onChange={(e: any) => setReproveReason(e.target.value)}
              placeholder="Informe ao usuário a razão da reprovação da sua solicitação de acesso."
            ></S.InputReason>
          )}
          <S.Buttons className="top-margin">
            <S.IconButton
              className="text"
              onClick={() => setOpenAnalysisModal(false)}
            >
              Cancelar
            </S.IconButton>
            {isApprove && (
              <S.IconButton
                className="success"
                onClick={approveUser}
                disabled={isLoadingAPICall}
              >
                {isLoadingAPICall ? <LoadingCircle /> : 'Aprovar'}
              </S.IconButton>
            )}
            {!isApprove && (
              <S.IconButton
                className={!reproveReason ? 'disabled danger' : 'danger'}
                onClick={repproveUser}
                disabled={isLoadingAPICall || !reproveReason}
              >
                {isLoadingAPICall ? <LoadingCircle /> : 'Reprovar'}
              </S.IconButton>
            )}
          </S.Buttons>
        </ModalResponsive>

        <ModalResponsive
          title={'Desativação de usuário'}
          isOpenModal={openDeactivateModal}
          handleOpenModal={setOpenDeactivateModal}
        >
          <S.Text>
            Você está desativando o usuário{' '}
            <S.Text className="bold inline">
              {user?.nome ?? user?.nomeContato ?? user?.razaoSocial}
            </S.Text>
            . Essa ação bloqueará o acesso ao sistema. Deseja continuar?
          </S.Text>
          <S.Buttons>
            <S.IconButton
              className="text"
              onClick={() => setOpenDeactivateModal(false)}
            >
              Cancelar
            </S.IconButton>
            <S.IconButton
              className="danger"
              onClick={deactivateUser}
              disabled={isLoadingAPICall}
            >
              {isLoadingAPICall ? <LoadingCircle /> : 'Desativar'}
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>
        <ModalResponsive
          title={'Reativação de usuário'}
          isOpenModal={openReactivateModal}
          handleOpenModal={setOpenReactivateModal}
        >
          <S.Text>
            Você está reativando o usuário{' '}
            <S.Text className="bold inline">
              {user?.nome ?? user?.nomeContato ?? user?.razaoSocial}
            </S.Text>
            . Essa ação concederá o acesso ao sistema. Deseja continuar?
          </S.Text>
          <S.Buttons>
            <S.IconButton
              className="text"
              onClick={() => setOpenReactivateModal(false)}
            >
              Cancelar
            </S.IconButton>
            <S.IconButton
              className="success"
              onClick={activateUser}
              disabled={isLoadingAPICall}
            >
              {isLoadingAPICall ? <LoadingCircle /> : 'Ativar'}
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>

        <ModalResponsive
          title={'Cadastro de cliente final'}
          isOpenModal={openRegisterModal}
          handleOpenModal={setOpenRegisterModal}
        >
          <RegistrationClientForm onCancel={handleModalClose} />
        </ModalResponsive>

        <ModalResponsive
          title={'Transformar usuário em admin'}
          isOpenModal={openAdminModal}
          handleOpenModal={setOpenAdminModal}
        >
          <S.Text>
            Você tem certeza que deseja transformar o usuário{' '}
            <S.Text className="bold inline">
              {user?.nome ?? user?.nomeContato ?? user?.razaoSocial}
            </S.Text>{' '}
            em admin? Essa ação concederá privilégios administrativos ao
            usuário.
          </S.Text>
          <S.Buttons>
            <S.IconButton
              className="text"
              onClick={() => setOpenAdminModal(false)}
            >
              Cancelar
            </S.IconButton>
            <S.IconButton
              className="success"
              onClick={handleMakeAdmin}
              disabled={isLoadingAPICall}
            >
              {isLoadingAPICall ? <LoadingCircle /> : 'Confirmar'}
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>

        <ModalResponsive
          title={'Confirmação de exclusão de usuário'}
          isOpenModal={openDeleteModal}
          handleOpenModal={setOpenDeleteModal}
        >
          <S.Text>
            Você tem certeza que deseja excluir o usuário{' '}
            <S.Text className="bold inline">
              {user?.nome ?? user?.nomeContato ?? user?.razaoSocial}
            </S.Text>
            . <br />
            Essa ação não poderá ser revertida.
          </S.Text>
          <S.Buttons>
            <S.IconButton
              className="text"
              onClick={() => setOpenDeleteModal(false)}
            >
              Cancelar
            </S.IconButton>
            <S.IconButton
              className="danger"
              onClick={() => handleDeleteUser(user.id)}
              disabled={isLoadingAPICall}
            >
              {isLoadingAPICall ? <LoadingCircle /> : 'Confirmar exclusão'}
            </S.IconButton>
          </S.Buttons>
        </ModalResponsive>
      </>

      <ToastContainer
        closeButton={false}
        transition={Slide}
        position="bottom-center"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Container>
  );
};
